import { log } from '../services/production-logger';

/**
 * Centralized Firebase Configuration
 * 
 * This is the single source of truth for Firebase initialization in the app.
 * Follows official Firebase documentation patterns and React Native best practices.
 * 
 * @see https://firebase.google.com/docs/web/setup
 * @see https://docs.expo.dev/guides/using-firebase/
 */

import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { 
  Auth, 
  getAuth, 
  initializeAuth, 
  browserLocalPersistence,
  setPersistence,
  inMemoryPersistence
} from 'firebase/auth';
import { 
  Firestore, 
  getFirestore, 
  connectFirestoreEmulator,
  enableMultiTabIndexedDbPersistence,
  enableNetwork,
  disableNetwork
} from 'firebase/firestore';
import { FirebaseStorage, getStorage, connectStorageEmulator } from 'firebase/storage';
import { Functions, getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

// Import getReactNativePersistence only for React Native platforms
let getReactNativePersistence: any;
if (Platform.OS !== 'web') {
  try {
    getReactNativePersistence = require('firebase/auth').getReactNativePersistence;
  } catch (error) {
    log.warn('getReactNativePersistence not available, using fallback');
  }
}
import { env, logEnvironmentInfo } from '../config/env';
import { getWebFirebaseConfig } from '../config/firebase-config.web';

// Log environment info in development
if (__DEV__) {
  logEnvironmentInfo();
}

// Debug: Log raw environment access for web
if (Platform.OS === 'web' && __DEV__) {
  log.debug('🔍 Debug: Raw Firebase env values:', {
    apiKey: env.FIREBASE_API_KEY?.substring(0, 10) + '...',
    projectId: env.FIREBASE_PROJECT_ID,
    authDomain: env.FIREBASE_AUTH_DOMAIN,
    hasApiKey: !!env.FIREBASE_API_KEY,
    hasProjectId: !!env.FIREBASE_PROJECT_ID,
  });
}

// Get web fallback config if needed
const webFallbackConfig = getWebFirebaseConfig();

// Firebase configuration from environment variables or web fallback
const firebaseConfig = webFallbackConfig || {
  apiKey: env.FIREBASE_API_KEY,
  authDomain: env.FIREBASE_AUTH_DOMAIN,
  projectId: env.FIREBASE_PROJECT_ID,
  storageBucket: env.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
  appId: env.FIREBASE_APP_ID,
  measurementId: env.FIREBASE_MEASUREMENT_ID,
};

// Check if we have valid Firebase configuration
const hasValidConfig = firebaseConfig.apiKey && 
  firebaseConfig.projectId && 
  firebaseConfig.apiKey !== 'development-fallback' &&
  firebaseConfig.projectId !== 'development-fallback';

if (!hasValidConfig) {
  // For web builds in development, provide more helpful guidance
  if (Platform.OS === 'web' && __DEV__) {
    log.warn('🔥 Firebase configuration is missing or invalid for web development.');
    log.warn('🔧 To enable authentication and data features:');
    log.warn('   1. Copy .env.example to .env');
    log.warn('   2. Add your Firebase project configuration');
    log.warn('   3. Restart the development server');
    log.warn('📱 Running in demo mode - some features will be limited.');
  } else {
    throw new Error(
      'Firebase configuration is missing required fields. Please check your environment variables.',
    );
  }
}

// Initialize Firebase App using singleton pattern
let app: FirebaseApp | null = null;

// Only initialize Firebase if we have valid configuration
if (hasValidConfig) {
  try {
    if (!getApps().length) {
      log.debug('🔥 Initializing Firebase app...');
      app = initializeApp(firebaseConfig);
    } else {
      log.debug('🔥 Using existing Firebase app');
      app = getApp();
    }
  } catch (error) {
    log.error('❌ Failed to initialize Firebase app:', error);
    throw error;
  }
} else if (Platform.OS === 'web' && __DEV__) {
  log.warn('⚠️ Skipping Firebase initialization - invalid configuration');
  app = null;
}

// Initialize Auth with platform-specific persistence
let auth: Auth | null = null;

if (app) {
  try {
    if (Platform.OS === 'web') {
      // For web, use standard getAuth
      auth = getAuth(app);
      log.debug('🔐 Using web auth instance');
      // Set persistence for web
      setPersistence(auth, browserLocalPersistence).catch((error) => {
        log.warn('Failed to set auth persistence for web:', error);
      });
    } else {
      // For React Native, initialize auth with AsyncStorage persistence
      log.debug('🔐 Initializing auth with AsyncStorage persistence');
      if (getReactNativePersistence && AsyncStorage) {
        auth = initializeAuth(app, {
          persistence: getReactNativePersistence(AsyncStorage),
        });
      } else {
        // Fallback if React Native persistence is not available
        log.warn('⚠️ React Native persistence not available, using getAuth fallback');
        auth = getAuth(app);
      }
    }
  } catch (error) {
    log.error('❌ Error initializing auth:', error);
    
    // If initializeAuth fails, try getAuth as fallback
    try {
      auth = getAuth(app);
      log.warn('⚠️ Using getAuth fallback');
    } catch (fallbackError) {
      log.error('❌ Fallback auth initialization also failed:', fallbackError);
      // Last resort: in-memory persistence
      auth = initializeAuth(app, {
        persistence: inMemoryPersistence,
      });
      log.warn('⚠️ Auth initialized with in-memory persistence (state will not persist)');
    }
  }
} else {
  log.warn('⚠️ Skipping auth initialization - no Firebase app available');
}

// Initialize Firestore with better error handling
let firestore: Firestore | null = null;
if (app) {
  try {
    firestore = getFirestore(app);
    log.debug('🔥 Firestore initialized successfully');
  } catch (error) {
    log.error('❌ Failed to initialize Firestore:', error);
    throw error;
  }
} else {
  log.warn('⚠️ Skipping Firestore initialization - no Firebase app available');
}

// Enable offline persistence for web
if (Platform.OS === 'web' && typeof window !== 'undefined' && firestore) {
  enableMultiTabIndexedDbPersistence(firestore).catch((err) => {
    if (err.code === 'failed-precondition') {
      log.warn('Multiple tabs open, Firestore persistence disabled');
    } else if (err.code === 'unimplemented') {
      log.warn('Browser doesn\'t support Firestore persistence');
    }
  });
}

// Initialize Storage
let storage: FirebaseStorage | null = null;
if (app) {
  try {
    storage = getStorage(app);
  } catch (error) {
    log.error('❌ Failed to initialize Firebase Storage:', error);
    if (Platform.OS === 'web' && __DEV__) {
      log.warn('⚠️ Storage not available in development web build');
    }
  }
} else {
  log.warn('⚠️ Skipping Storage initialization - no Firebase app available');
}

// Initialize Functions
let functions: Functions | null = null;
if (app) {
  try {
    functions = getFunctions(app, 'us-central1');
  } catch (error) {
    log.error('❌ Failed to initialize Firebase Functions:', error);
    if (Platform.OS === 'web' && __DEV__) {
      log.warn('⚠️ Functions not available in development web build');
    }
  }
} else {
  log.warn('⚠️ Skipping Functions initialization - no Firebase app available');
}

// Connect to emulators in development (if configured)
if (__DEV__ && env.USE_FIREBASE_EMULATOR === 'true' && hasValidConfig) {
  log.debug('🔧 Connecting to Firebase emulators...');
  
  // Firestore emulator
  if (env.FIRESTORE_EMULATOR_HOST && firestore) {
    const [host, port] = env.FIRESTORE_EMULATOR_HOST.split(':');
    connectFirestoreEmulator(firestore, host, parseInt(port, 10));
  }
  
  // Auth emulator
  if (env.AUTH_EMULATOR_HOST && auth) {
    const authEmulatorUrl = `http://${env.AUTH_EMULATOR_HOST}`;
    // Note: connectAuthEmulator is not imported but would be used here
    log.debug('Auth emulator URL:', authEmulatorUrl);
  }
  
  // Storage emulator
  if (env.STORAGE_EMULATOR_HOST && storage) {
    const [host, port] = env.STORAGE_EMULATOR_HOST.split(':');
    connectStorageEmulator(storage, host, parseInt(port, 10));
  }
  
  // Functions emulator
  if (env.FUNCTIONS_EMULATOR_HOST && functions) {
    const [host, port] = env.FUNCTIONS_EMULATOR_HOST.split(':');
    connectFunctionsEmulator(functions, host, parseInt(port, 10));
  }
}

log.debug(`✅ Firebase initialized for ${Platform.OS}`);

// Export initialized instances
export { app, auth, firestore, storage, functions };

// Convenience exports for common Firestore operations
export { 
  enableNetwork, 
  disableNetwork,
  enableNetwork as enableFirestoreNetwork,
  disableNetwork as disableFirestoreNetwork 
} from 'firebase/firestore';

// Type exports for strong typing
export type { FirebaseApp, Auth, Firestore, FirebaseStorage, Functions };

// Helper functions for service compatibility
export const getDb = () => firestore;
export const getFirebaseAuth = () => auth;
export const getFirebaseStorage = () => storage;
export const getFirebaseFunctions = () => functions;

// For backward compatibility with services expecting getFirebaseInstances
export const getFirebaseInstances = () => ({
  app,
  auth,
  firestore,
  storage,
  functions,
});
